import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useRoleController } from "@/components/useRoleController";
import { PROVIDER_PATHS } from "@/constants/paths";
import useDisclosure, { DisclosureHost } from "@/hooks/use-disclosure";
import { useSnackbar } from "@/hooks/useSnackbar";
import { useGroupSessionById } from "@/queries/group-visit/use-group-session-by-id";
import { GROUP_SESSION_LIST_QUERY_KEY } from "@/queries/group-visit/use-group-sessions";
import { useUpdateGroupSessionStatus } from "@/queries/group-visit/use-update-group-session-status";
import { UpdateSessionStatusSessionStatusEnum } from "@/services";
import { getSuccessSnackbar } from "@/utils/utils";
import {
  APPOINTMENT_ROLE,
  SESSION_ID_QUERY_PARAM,
} from "../group-appointment-constant";
import { GroupSessionDetail } from "../group-appointment-types";

export type UseGroupSessionActions = {
  detail?: GroupSessionDetail;
  isLoading: boolean;
  cancelDisclosure: DisclosureHost;
  cancelSession: () => Promise<unknown>;
  rescheduleDisclosure: DisclosureHost;
  canReschedule: boolean;
  finishReschedule: () => void;
  completeCheckIn: () => Promise<unknown>;
  completeVisit: () => void;
  viewSummary: () => void;
};

const withSession = (path: string, sessionId: string) =>
  `${path}?${SESSION_ID_QUERY_PARAM}=${sessionId}`;

/**
 * Footer actions of the group session drawer — the group counterparts of the
 * individual appointment's status changes.
 */
export const useGroupSessionActions = (
  sessionId: string | undefined,
  onDone: () => void
): UseGroupSessionActions => {
  const client = useQueryClient();
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const cancelDisclosure = useDisclosure();
  const rescheduleDisclosure = useDisclosure();
  const { hasUpdate: hasUpdateAppointment } = useRoleController(
    APPOINTMENT_ROLE.SCHEDULING
  );
  const { hasUpdate: hasUpdateRescheduleAndCancel } = useRoleController(
    APPOINTMENT_ROLE.RESCHEDULE_AND_CANCEL
  );
  const { data: detail, isLoading } = useGroupSessionById(sessionId);
  const { mutateAsync: updateStatus } = useUpdateGroupSessionStatus();

  const changeStatus = useCallback(
    async (sessionStatus: UpdateSessionStatusSessionStatusEnum) => {
      if (!sessionId) return;
      const res = await updateStatus({ sessionId, sessionStatus });
      showSnackbar(getSuccessSnackbar(res));
      client.invalidateQueries({ queryKey: [GROUP_SESSION_LIST_QUERY_KEY] });
      client.invalidateQueries({ queryKey: ["appointment-list-v2"] });
    },
    [sessionId, updateStatus, showSnackbar, client]
  );

  const cancelSession = useCallback(async () => {
    await changeStatus(UpdateSessionStatusSessionStatusEnum.Cancelled);
    cancelDisclosure.close();
    onDone();
  }, [changeStatus, cancelDisclosure, onDone]);

  const finishReschedule = useCallback(() => {
    rescheduleDisclosure.close();
    onDone();
  }, [rescheduleDisclosure, onDone]);

  // Only Start Session on the check-in screen moves the session to In Exam.
  const completeCheckIn = useCallback(async () => {
    if (!sessionId) return;
    onDone();
    navigate(withSession(PROVIDER_PATHS.GROUP_CHECK_IN, sessionId));
  }, [sessionId, onDone, navigate]);

  const completeVisit = useCallback(() => {
    if (!sessionId) return;
    onDone();
    navigate(withSession(PROVIDER_PATHS.GROUP_SESSION_NOTE, sessionId));
  }, [sessionId, onDone, navigate]);

  return {
    detail,
    isLoading,
    cancelDisclosure,
    cancelSession,
    rescheduleDisclosure,
    canReschedule: hasUpdateAppointment && hasUpdateRescheduleAndCancel,
    finishReschedule,
    completeCheckIn,
    completeVisit,
    viewSummary: completeVisit,
  };
};
